/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import { Fragment, useEffect, useMemo } from 'react';

import { useIntl } from 'react-intl';
import { graphql, useLazyLoadQuery } from 'react-relay';

import { cssMap, jsx } from '@atlaskit/css';
import ScreenIcon from '@atlaskit/icon/core/screen';
import { token } from '@atlaskit/tokens';
import { SearchResult } from '@atlassian/search-dialog/search-result';

import { messages } from '../messages';
import type { portalResultsQuery } from './__generated__/portalResultsQuery.graphql';
import {
	InternalLinkComponent,
	SearchResultContainer,
	SearchResultSection,
} from './search-result-section';
import { SearchResultType, type SearchQueryProps } from './types';
import { useSearchResultAnalytics } from './use-search-result-analytics';
import { getResultStatus } from './utils';

const DEFAULT_RESULTS_LIMIT = 3;

export const PortalResults = ({
	cloudId,
	helpCenterAri,
	onStatus,
	queryWord,
}: SearchQueryProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const data = useLazyLoadQuery<portalResultsQuery>(
		graphql`
			query portalResultsQuery(
				$cloudId: ID!
				$helpCenterAri: ID
				$queryTerm: String!
				$limit: Int!
			) {
				helpObjectStore_searchPortals(
					cloudId: $cloudId
					queryTerm: $queryTerm
					limit: $limit
					helpCenterAri: $helpCenterAri
				) {
					... on HelpObjectStorePortalSearchResults {
						results {
							__typename
							id
							title
							displayLink
							iconUrl
						}
					}
					... on HelpObjectStoreSearchError {
						message
					}
				}
			}
		`,
		{
			cloudId,
			helpCenterAri,
			queryTerm: queryWord,
			limit: DEFAULT_RESULTS_LIMIT,
		},
	);
	const errorMessage = data.helpObjectStore_searchPortals?.message;
	const portals = useMemo(
		() =>
			data.helpObjectStore_searchPortals?.results?.filter(
				(portal) => portal.__typename === 'HelpObjectStorePortalSearchResult',
			) ?? [],
		[data.helpObjectStore_searchPortals?.results],
	);
	const resultStatus = getResultStatus(errorMessage, portals.length);

	useSearchResultAnalytics({
		resultCount: portals.length,
		resultStatus,
		resultType: SearchResultType.Portals,
	});

	useEffect(() => {
		onStatus(resultStatus);
	}, [onStatus, resultStatus]);

	if (portals.length === 0 || errorMessage) {
		return <Fragment />;
	}

	return (
		<SearchResultSection heading={formatMessage(messages.teamPortalsHeading)}>
			{portals.map((portal) => (
				<SearchResultContainer key={portal.id}>
					<SearchResult
						href={portal.displayLink}
						linkComponent={InternalLinkComponent}
						title={portal.title}
						isCollapsed
						icon={
							portal.iconUrl ? (
								<img css={styles.icon} src={portal.iconUrl} alt="" />
							) : (
								<ScreenIcon label="" spacing="spacious" color={token('color.text.subtle')} />
							)
						}
					/>
				</SearchResultContainer>
			))}
		</SearchResultSection>
	);
};

const styles = cssMap({
	icon: {
		width: '24px',
		height: '24px',
		objectFit: 'contain',
	},
});
